"use client";

import React from "react";
import { motion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, TrendingUp, UserCheck, Search, Award, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const trustPoints = [{
  title: "100% Direct Consultant Access",
  description: "You work directly with me, Yassine Essaadi. No junior account managers, sales representatives, or outsourced work."
}, {
  title: "Preston & Lancashire Expertise",
  description: "Deep understanding of local search behavior, Map Pack signals, and regional competitor benchmarks."
}, {
  title: "Transparent & Honest Growth",
  description: "Clear deliverables, direct phone access, and straightforward reporting focused on actual conversions."
}];
const localServices = ["Google Map Pack Dominance", "Hyper-Local Citation Building", "Technical Local Audit", "Geo-Targeted Content"];
export default function Localhero() {
  return <section id="localherohighauthority" data-nav="dark" className="relative bg-background text-foreground py-10 sm:py-16 lg:py-20 overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl bg-primary text-primary-foreground p-6 sm:p-10 lg:p-14 shadow-2xl overflow-hidden border border-primary/20">
          
          {/* Background ambient glows */}
          <div className="absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/20 blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center relative z-10">
            
            {/* Left Column: Core Value Proposition */}
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="lg:col-span-7 flex flex-col items-start text-left">
              {/* Pill Badge */}
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent/20 border border-accent/30 text-accent-foreground text-xs sm:text-sm font-medium mb-6">
                <MapPin className="size-4 text-accent" />
                <span>Local SEO Consultant in Preston & Lancashire</span>
              </div>

              {/* Display Headline */}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15] mb-6">
                Local SEO Consultant in Preston
              </h2>

              {/* Supporting Lead Copy */}
              <p className="text-primary-foreground/90 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl font-normal">
                I help Preston and Lancashire businesses claim top local Google rankings, dominate the Map Pack, and turn organic search visibility into sustainable client growth. <strong className="text-accent-foreground font-semibold">You work directly with me</strong>—ensuring tailored strategies and direct accountability.
              </p>

              {/* Local Deliverables Chips */}
              <div className="flex flex-wrap gap-2.5 mb-8">
                {localServices.map((service, i) => <div key={service} data-index={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-foreground/10 border border-primary-foreground/15 text-xs sm:text-sm text-primary-foreground font-medium">
                    <CheckCircle2 className="size-4 text-accent shrink-0" />
                    <span>{service}</span>
                  </div>)}
              </div>

              {/* Primary Actions */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-8">
                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold px-8 py-6 text-base rounded-xl">
                  <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                    <span>Book a Free SEO Consultation</span>
                    <ArrowRight className="size-5" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium px-6 py-6 text-base rounded-xl">
                  <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                    <Search className="size-4" />
                    <span>Explore My Case Studies</span>
                  </Link>
                </Button>
              </div>

              {/* Micro Trust Indicators */}
              <div className="pt-6 border-t border-primary-foreground/15 w-full grid grid-cols-3 gap-3 text-left">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <UserCheck className="size-5 text-accent shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-primary-foreground">Direct Access</p>
                    <p className="text-[13px] text-primary-foreground/70">1-on-1 with Yassine</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <ShieldCheck className="size-5 text-accent shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-primary-foreground">No Lock-in</p>
                    <p className="text-[13px] text-primary-foreground/70">Transparent retainer</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                  <TrendingUp className="size-5 text-accent shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-primary-foreground">Local Growth</p>
                    <p className="text-[13px] text-primary-foreground/70">Measurable leads</p>
                  </div>
                </div>
              </div>

            </motion.div>

            {/* Right Column: Authentic Consultant Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1,
            ease: "easeOut"
          }} className="lg:col-span-5 relative flex justify-center">
              <div className="relative w-full max-w-md">
                
                {/* Consultant Frame */}
                <div className="relative rounded-2xl bg-card text-card-foreground p-3.5 shadow-xl border border-border">
                  <div className="aspect-[4/5] rounded-xl overflow-hidden relative bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in Preston" className="object-cover object-top size-full" loading="eager" />
                    
                    {/* Floating Namecard */}
                    <div className="absolute bottom-3 left-3 right-3 bg-card/95 backdrop-blur-md border border-border p-3 rounded-lg shadow-md flex items-center gap-3">
                      <div className="size-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0">
                        <Award className="size-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-card-foreground">Yassine Essaadi</p>
                        <p className="text-[13px] text-muted-foreground">Independent SEO Specialist</p>
                      </div>
                    </div>
                  </div>

                  {/* Consultant Direct Quote */}
                  <div className="mt-3 p-3 rounded-xl bg-muted/60 border border-border">
                    <div className="flex items-start gap-2.5">
                      <Sparkles className="size-4 text-accent shrink-0 mt-0.5" />
                      <p className="text-xs text-card-foreground leading-snug">
                        <strong className="font-semibold text-foreground">“No account managers or fluff.</strong> I build, execute, and refine your Preston local search strategy myself.”
                      </p>
                    </div>
                  </div>

                </div>

                {/* Regional Badge */}
                <div className="absolute -top-3 -right-3 bg-accent text-accent-foreground rounded-xl px-3.5 py-1.5 shadow-lg border border-accent/40 text-xs font-semibold flex items-center gap-1.5">
                  <MapPin className="size-3.5" />
                  <span>Preston & Lancashire</span>
                </div>

              </div>
            </motion.div>

          </div>

          {/* Bottom Row: 3 Core Pillars */}
          <div className="mt-10 pt-8 border-t border-primary-foreground/15 grid grid-cols-1 md:grid-cols-3 gap-4">
            {trustPoints.map((item, i) => <div key={item.title} data-index={i} className="p-4 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10 hover:bg-primary-foreground/10 transition-colors">
                <h2 className="font-serif text-sm font-semibold text-primary-foreground mb-1 flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-accent" />
                  <span>{item.title}</span>
                </h2>
                <p className="text-xs text-primary-foreground/80 leading-relaxed">
                  {item.description}
                </p>
              </div>)}
          </div>

        </div>
      </div>
    </section>;
}